import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useIndicatorPosition } from './useIndicatorPosition';

type TabProps<T extends string | number> = {
  value: T;
  label: React.ReactNode;
};

interface ITabsProps<T extends string | number> {
  className?: string;
  loading?: boolean;
  options?: TabProps<T>[];
  value?: T;
  onChange?: (value: T) => void;
}

const fixPx = (value: number) => (value === 0 ? '0' : value + 'px');

export const Tabs = <T extends string | number>(props: ITabsProps<T>) => {
  const [indexState, setIndex] = useState(0);
  const refContainer = useRef<HTMLUListElement>(null);

  const index = useMemo(() => {
    if (props.value && props.options) return props.options.findIndex((item) => item.value === props.value);
    else return indexState;
  }, [props.value, props.options, indexState]);

  const { scale, shift, ready } = useIndicatorPosition({ index, ref: refContainer });

  const indicatorStyles = useMemo(
    () => ({
      transform: `translateX(${fixPx(shift)}) scaleX(${scale})`,
    }),
    [scale, shift],
  );

  return (
    <ul className={clsx('flex items-stretch gap-8 h-7 list-none m-0 py-1 relative select-none', props.className)} ref={refContainer}>
      {props.options?.map((item, idx) => (
        <li
          key={idx}
          className={clsx('cursor-pointer opacity-50 transition-opacity duration-300', idx === index && 'cursor-default opacity-100')}
          onClick={() => (props.onChange ? props.onChange(item.value) : setIndex(idx))}
        >
          {item.label}
        </li>
      ))}
      <span
        className={clsx(
          'bg-primary rounded border-none absolute w-full h-[2px] bottom-0 transform origin-left',
          ready && 'transition-transform duration-300',
        )}
        style={indicatorStyles}
      />
    </ul>
  );
};
